import type { UserInfo } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting, getRoute } from "Infrastructure/Utils/UrlUtils";
import { Stack, useTheme } from "@mui/material";
import { Bell, ChevronDown, Plus } from "lucide-react";
import { useState } from "react";
import blogicLogo from "../../../Assets/Images/blogicLogo.svg";
import { CreateQuestForm } from "../Forms/QuestForms/CreateEditQuestForm";
import { CreateRewardForm } from "../Forms/RewardForms/CreateEditRewardForm";
import { DropdownMenu } from "./DropdownMenu";
import { NavbarButton } from "./NavbarButton";
import { UserLogoutDropdownMenu } from "./UserLogoutDropdownMenu";

type NavbarProps = {
  user: UserInfo;
};

export const Navbar = ({ user }: NavbarProps) => {
  const { colors, customSpacing } = useTheme();
  const { t } = useResource();
  const [isCreateQuestOpen, setIsCreateQuestOpen] = useState(false);
  const [isCreateRewardOpen, setIsCreateRewardOpen] = useState(false);

  const userRole = user.role;

  const links = [
    {
      path: getRoute(AppRouting.Dashboard),
      label:
        userRole === "GameMaster"
          ? t(Resources.Common.Dashboard)
          : t(Resources.Common.Profile),
      isDisabled: false,
    },
    {
      path: getRoute(AppRouting.Players),
      label: t(Resources.Common.Players),
      isDisabled: userRole !== "GameMaster",
    },
    {
      path: getRoute(AppRouting.Quests),
      label: t(Resources.Common.Quests),
      isDisabled: false,
    },
    {
      path: getRoute(AppRouting.Challenges),
      label: t(Resources.Common.Challenges),
      isDisabled: userRole !== "GameMaster",
    },
    {
      path: getRoute(AppRouting.Rewards),
      label: t(Resources.Common.Rewards),
      isDisabled: false,
    },
    {
      path: getRoute(AppRouting.Requests),
      label: t(Resources.Common.Requests),
      isDisabled: userRole !== "GameMaster",
    },
    {
      path: getRoute(AppRouting.Leaderboard),
      label: t(Resources.Common.Leaderboard),
      isDisabled: userRole === "GameMaster",
    },
  ];

  const visibleLinks = links.filter(({ isDisabled }) => !isDisabled);

  return (
    <Stack
      component="nav"
      direction="row"
      sx={{
        padding: customSpacing["2xl"],
        gap: customSpacing.xl,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <img src={blogicLogo} alt="Blogic logo" width={55} height={24} />
      </Stack>

      <Stack
        direction="row"
        sx={{
          gap: customSpacing.md,
          marginLeft: "auto",
        }}
      >
        {visibleLinks.map((link) => (
          <NavbarButton key={link.path} href={link.path} label={link.label} />
        ))}
      </Stack>

      <Stack
        direction="row"
        sx={{
          marginLeft: "auto",
          gap: customSpacing.md,
        }}
      >
        {userRole === "GameMaster" && (
          <DropdownMenu
            buttonLabel={t(Resources.Common.CreateNew)}
            menuTitle={t(Resources.Common.CreateNew)}
            icon={<ChevronDown size={16} color={colors.text.inverse} />}
            items={[
              {
                label: t(Resources.Common.Quest),
                icon: <Plus size={16} />,
                onClick: () => setIsCreateQuestOpen(true),
              },
              // TODO[KP]: Change href to onClick when creating challenges is ready
              {
                label: t(Resources.Common.Challenge),
                href: getRoute(AppRouting.AddChallenge),
                icon: <Plus size={16} />,
              },
              {
                label: t(Resources.Common.Reward),
                icon: <Plus size={16} />,
                onClick: () => setIsCreateRewardOpen(true),
              },
            ]}
          />
        )}

        <Stack
          direction="row"
          sx={{
            marginLeft: "auto",
            gap: customSpacing.md,
          }}
        >
          <NavbarButton
            href={getRoute(AppRouting.Notifications)}
            customWidth={40}
            icon={<Bell size={16} color={colors.text.primary} />}
          />

          <UserLogoutDropdownMenu
            picture={user.picture}
            firstName={user.firstName}
            lastName={user.lastName}
            name={`${user.firstName} ${user.lastName}`}
            email={user.email}
          />
        </Stack>
      </Stack>
      <CreateQuestForm
        mode="create"
        open={isCreateQuestOpen}
        onClose={() => setIsCreateQuestOpen(false)}
        onSuccess={() => setIsCreateQuestOpen(false)}
      />
      <CreateRewardForm
        mode="create"
        open={isCreateRewardOpen}
        onClose={() => setIsCreateRewardOpen(false)}
        onSuccess={() => setIsCreateRewardOpen(false)}
      />
    </Stack>
  );
};
